import React, { useEffect, useMemo, useState } from 'react';
import apiKeyService, { ApiUsageLog, formatRelativeTime, getStatusColor } from '../../services/apiKeyService';
import { ChevronLeft, ChevronRight, Filter, RefreshCcw } from 'lucide-react';

interface ApiKeyLogsProps {
  apiKeyId: string;
  onBack?: () => void;
}

const PAGE_SIZE = 25;

const methods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const ApiKeyLogs: React.FC<ApiKeyLogsProps> = ({ apiKeyId, onBack }) => {
  const [logs, setLogs] = useState<ApiUsageLog[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [methodFilter, setMethodFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const offset = useMemo(() => (page - 1) * PAGE_SIZE, [page]);
  const totalPages = useMemo(() => (total > 0 ? Math.ceil(total / PAGE_SIZE) : 1), [total]);

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeyId, offset]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
  const data = await apiKeyService.getUsageLogs(apiKeyId, PAGE_SIZE, offset);
  setLogs(data.logs || []);
  setTotal(typeof data.total === 'number' ? data.total : (data.logs?.length || 0));
    } catch (err: any) {
      console.error('Failed to load API key logs', err);
      setError(err?.response?.data?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    await loadLogs();
  };

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (methodFilter && log.method !== methodFilter) return false;
      if (statusFilter) {
        const code = String(log.statusCode);
        if (statusFilter.endsWith('xx')) {
          if (code[0] + 'xx' !== statusFilter) return false;
        } else if (code !== statusFilter) {
          return false;
        }
      }
      if (search) {
        const q = search.toLowerCase();
        if (!log.endpoint.toLowerCase().includes(q) && !(log.error || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [logs, methodFilter, statusFilter, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="text-indigo-600 hover:text-indigo-700">
              ← Back
            </button>
          )}
          <h2 className="text-2xl font-bold text-gray-900">API Usage Logs</h2>
        </div>
        <button onClick={refresh} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
            <select
              value={methodFilter}
              onChange={(e) => {
                setPage(1);
                setMethodFilter(e.target.value);
              }}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">All</option>
              {methods.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">All</option>
              {['2xx', '3xx', '4xx', '5xx'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {[200, 201, 204, 301, 302, 400, 401, 403, 404, 429, 500].map((code) => (
                <option key={code} value={String(code)}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <div className="relative">
              <input
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="/api/posts, error message..."
                className="w-full px-3 py-2 border rounded-lg pr-10"
              />
              <Filter size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-6 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-6 py-6 text-center text-red-600">
                    {error}
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-6 text-center text-gray-500">
                    No logs found.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{formatRelativeTime(log.createdAt)}</td>
                    <td className="px-6 py-4 text-sm font-mono text-gray-900">{log.endpoint}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">{log.method}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(log.statusCode)}`}>
                        {log.statusCode}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{log.responseTime} ms</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{log.ipAddress || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 truncate max-w-[300px]" title={log.userAgent || ''}>
                      {log.userAgent || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-700 truncate max-w-[300px]" title={log.error || ''}>
                      {log.error || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 border rounded disabled:opacity-50"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-2 border rounded disabled:opacity-50"
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyLogs;
